import { Link } from "react-router-dom";
import heroImageWebp from "@/assets/hero-castle.jpg?format=webp&quality=80";
import heroImageSmWebp from "@/assets/hero-castle.jpg?format=webp&quality=80&w=800";
import heroImageFallback from "@/assets/hero-castle.jpg?quality=80";

export const HeroSection = () => {
  return (
    <section
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      aria-label="Hero">

      <div className="absolute inset-0 z-0">
        <picture>
          <source
            type="image/webp"
            srcSet={`${heroImageSmWebp} 800w, ${heroImageWebp} 1920w`}
            sizes="100vw"
          />
          <img
            src={heroImageFallback}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover scale-[1.03]"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{ objectPosition: "center 73%" }}
          />
        </picture>

        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(222,64%,8%)]/98 via-[hsl(222,64%,10%)]/95 to-[hsl(222,64%,14%)]/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222,64%,8%)]/95 via-[hsl(222,64%,8%)]/60 to-[hsl(222,64%,10%)]/50" />
      </div>

      <div className="absolute inset-0 z-[1] pointer-events-none" aria-hidden="true">
        <svg width="100%" height="100%" className="absolute inset-0 opacity-70">
          {[
          [8, 12], [15, 28], [22, 8], [30, 42], [38, 17],
          [45, 33], [52, 7], [60, 48], [67, 22], [74, 36],
          [81, 13], [88, 45], [93, 27], [4, 55], [18, 62],
          [27, 70], [35, 58], [43, 73], [51, 61], [58, 80],
          [65, 54], [72, 68], [78, 76], [85, 60], [91, 82],
          [12, 85], [24, 91], [47, 88], [63, 92], [79, 86],
          [96, 10], [3, 38], [56, 25], [83, 52], [40, 82]].
          map(([cx, cy], i) =>
          <circle
            key={i}
            cx={`${cx}%`}
            cy={`${cy}%`}
            r={i % 7 === 0 ? "1.5" : i % 3 === 0 ? "1" : "0.6"}
            fill={i % 5 === 0 ? "hsl(46,67%,70%)" : "hsl(42,60%,90%)"}
            opacity={0.3 + i % 5 * 0.12}
            style={{
              animation: `twinkle ${2.5 + i % 4 * 0.8}s ease-in-out ${i % 6 * 0.4}s infinite`
            }} />

          )}
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10 pb-32">
        <div className="max-w-[680px]">
          <div className="flex items-center gap-3 mb-8 animate-fade-in">
            <div className="h-px w-8 bg-gold/60" />
            <p className="font-cinzel text-[0.6rem] text-gold tracking-[0.35em] uppercase">
              Walt Disney World® &amp; Beyond
            </p>
          </div>

          <h1 className="font-serif font-light text-foreground leading-[1.05] mb-8 animate-fade-in-delay-1"
          style={{ fontSize: "clamp(3.2rem, 7vw, 5.5rem)" }}>
            Walt Disney World Vacation Planning for Stress-Free Family Travel
          </h1>

          <p className="font-sans text-[1.1rem] text-foreground/65 leading-[1.7] max-w-[500px] mb-12 animate-fade-in-delay-2">
            Specializing in Disney. Dream Vacations and Beyond.
Every reservation. Every detail. Handled.
You show up ready. You leave already planning the next one.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-delay-3">
            <a
              href="https://book.peacefullyeveraftertravel.com"
              className="inline-flex items-center justify-center gap-2 font-cinzel text-[0.62rem] tracking-[0.25em] uppercase px-8 py-4 bg-gold hover:bg-[hsl(46,80%,60%)] text-[hsl(222,64%,10%)] rounded-[2px] shadow-gold transition-smooth">

              Start Planning
            </a>
            <Link
              to="/destinations"
              className="inline-flex items-center justify-center gap-2 font-cinzel text-[0.62rem] tracking-[0.25em] uppercase px-8 py-4 border border-foreground/20 hover:border-gold/50 text-foreground/70 hover:text-foreground rounded-[2px] transition-smooth hover:bg-white/5">

              Explore Destinations
            </Link>
          </div>

          <div className="flex items-center gap-6 mt-12 pt-8 border-t border-white/10 animate-fade-in-delay-4">
            {[
            { stat: "100%", label: "Free to You" },
            { stat: "End-to-End", label: "Full Service" }].
            map((item) =>
            <div key={item.label} className="text-center">
                <div className="font-cinzel text-gold text-[0.75rem] tracking-[0.1em] font-semibold">{item.stat}</div>
                <div className="font-sans text-muted-foreground text-[0.65rem] mt-0.5 tracking-wide">{item.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-[5]" aria-hidden="true">
        <svg
          viewBox="0 0 1440 240"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: "block", width: "100%", height: "auto" }}>

          <path
            d={`
              M0,240 L0,180
              L30,180 L30,155 L38,155 L38,135 L44,135 L44,115 L50,115 L50,95
              L53,95 L53,75 L57,75 L57,55 L60,55 L60,35 L62,35 L62,15
              L64,15 L64,35 L66,35 L66,55 L69,55 L69,75 L73,75 L73,95
              L76,95 L76,115 L82,115 L82,135 L88,135 L88,155 L96,155 L96,180
              L120,180 L120,160 L128,160 L128,145 L135,145 L135,130 L142,130
              L142,145 L149,145 L149,160 L157,160 L157,180
              L190,180 L190,170 L195,170 L195,155 L200,155 L200,170 L205,170 L205,180
              L250,180 L250,170 L255,170 L255,155 L260,155 L260,145
              L266,145 L266,130 L272,130 L272,115 L278,115 L278,100
              L281,100 L281,85 L284,85 L284,65 L287,65 L287,48
              L289,48 L289,32 L291,32 L291,18 L293,18 L293,5
              L295,5 L295,18 L297,18 L297,32 L299,32 L299,48
              L301,48 L301,65 L304,65 L304,85 L307,85 L307,100
              L310,100 L310,115 L316,115 L316,130 L322,130 L322,145
              L328,145 L328,155 L333,155 L333,170 L338,170 L338,180
              L380,180 L380,170 L385,170 L385,160 L390,160 L390,170 L395,170 L395,180
              L440,180 L440,175 L445,175 L445,165 L450,165 L450,175 L455,175 L455,180
              L550,180 L550,170 L560,170 L560,160 L570,160 L570,170 L580,170 L580,180
              L650,180 L650,170 L658,170 L658,158 L666,158 L666,145 L674,145
              L674,132 L682,132 L682,118 L690,118 L690,105
              L694,105 L694,88 L698,88 L698,70 L701,70 L701,52
              L703,52 L703,36 L705,36 L705,20 L707,20 L707,8
              L709,8 L709,20 L711,20 L711,36 L713,36 L713,52
              L715,52 L715,70 L718,70 L718,88 L722,88 L722,105
              L726,105 L726,118 L734,118 L734,132 L742,132 L742,145
              L750,145 L750,158 L758,158 L758,170 L766,170 L766,180
              L820,180 L820,172 L826,172 L826,162 L832,162 L832,172 L838,172 L838,180
              L900,180 L900,172 L906,172 L906,165 L912,165 L912,172 L918,172 L918,180
              L1000,180 L1000,168 L1008,168 L1008,155 L1016,155 L1016,142 L1024,142
              L1024,130 L1030,130 L1030,118 L1036,118 L1036,108
              L1039,108 L1039,95 L1042,95 L1042,80 L1045,80 L1045,65
              L1047,65 L1047,48 L1049,48 L1049,32 L1051,32 L1051,16
              L1053,16 L1053,32 L1055,32 L1055,48 L1057,48 L1057,65
              L1059,65 L1059,80 L1062,80 L1062,95 L1065,95 L1065,108
              L1068,108 L1068,118 L1074,118 L1074,130 L1080,130 L1080,142
              L1088,142 L1088,155 L1096,155 L1096,168 L1104,168 L1104,180
              L1150,180 L1150,172 L1156,172 L1156,165 L1162,165 L1162,172 L1168,172 L1168,180
              L1230,180 L1230,168 L1236,168 L1236,158 L1242,158 L1242,168 L1248,168 L1248,180
              L1300,180 L1300,170 L1306,170 L1306,160 L1312,160 L1312,170 L1318,170 L1318,180
              L1370,180 L1370,170 L1376,170 L1376,162 L1382,162 L1382,170 L1388,170 L1388,180
              L1440,180 L1440,240 Z
            `}
            fill="hsl(222, 64%, 15%)" />

          <ellipse cx="293" cy="55" rx="3" ry="4" fill="hsl(46,67%,52%)" opacity="0.35" />
          <ellipse cx="707" cy="30" rx="3" ry="4" fill="hsl(46,67%,52%)" opacity="0.35" />
          <ellipse cx="1051" cy="45" rx="3" ry="4" fill="hsl(46,67%,52%)" opacity="0.35" />
        </svg>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent z-[6]" />
    </section>);

};